import { defineStore } from 'pinia'
import { fetchCmsCourses, fetchCmsCoursePage } from '@/services/courses'
import { getSessionVersion } from '@/auth/storage'

const inflight = new Map()

const keyForParams = (params = {}) => JSON.stringify([
  getSessionVersion(), Object.entries(params).filter(([, value]) => value !== undefined).sort(([a], [b]) => a.localeCompare(b)),
])

export const useCoursesStore = defineStore('courses', {
  state: () => ({
    cache: {},
    loading: {},
    error: null,
    pages: {},
    generation: 0,
  }),
  getters: {
    getCourses: (state) => (params = {}) => {
      const key = keyForParams(params)
      return state.cache[key]?.data || []
    },
    isLoading: (state) => (params = {}) => {
      const key = keyForParams(params)
      return Boolean(state.loading[key])
    },
  },
  actions: {
    loadCmsCoursePage(params = {}, { force = false, ttlMs = 60 * 1000 } = {}) {
      const key = `page:${keyForParams(params)}`
      const cached = this.pages[key]
      if (!force && cached && Date.now() - cached.fetchedAt < ttlMs) return Promise.resolve(cached.data)
      if (inflight.has(key)) return inflight.get(key)
      const generation = this.generation
      const promise = fetchCmsCoursePage(params).then(data => {
        if (generation === this.generation) this.pages[key] = { data, fetchedAt: Date.now() }
        return data
      }).finally(() => {
        if (inflight.get(key) === promise) inflight.delete(key)
      })
      inflight.set(key, promise)
      return promise
    },
    loadCmsCourses(params = {}, { force = false, ttlMs = 5 * 60 * 1000 } = {}) {
      const key = keyForParams(params)
      const cached = this.cache[key]
      const now = Date.now()

      if (!force && cached?.fetchedAt && (now - cached.fetchedAt) < ttlMs) {
        return Promise.resolve(cached.data)
      }

      const existing = inflight.get(key)
      if (existing) return existing

      this.loading[key] = true
      const p = fetchCmsCourses(params)
        .then((data) => {
          this.cache[key] = { data, fetchedAt: Date.now() }
          this.error = null
          return data
        })
        .catch((e) => {
          this.error = e
          throw e
        })
        .finally(() => {
          this.loading[key] = false
          inflight.delete(key)
        })

      inflight.set(key, p)
      return p
    },
    clear() {
      this.generation += 1
      this.pages = {}
      this.cache = {}
      this.loading = {}
      this.error = null
      inflight.clear()
    },
    prefetchPublicCourses() {
      return this.loadCmsCourses({}, { force: false })
    },
  },
})
